import { booleanPointInPolygon } from '@turf/turf';
import { Feature, Polygon, Position } from 'geojson';

import { ReportImpact, ReportTypeCategory } from '@/core/interfaces/common';
import { AppliedFilters, AutoDateRange, FilterType } from '@/core/interfaces/filters';
import {
  LocationCountryFilterItemValue,
  LocationFilterItemType,
  LocationGeofenceFilterItemValue,
} from '@/core/components/MapFilters/LocationFilter/LocationFilter.types';
import { DropdownValues } from '@/core/components/MapFilters/KeywordFilter/components';

import { AnalysisListItem } from '@/features/ChatBot/interfaces';

import { getTimeFrameFromAppliedFilters } from '@/utils/filters';
import { getTransformedLocationFilterData } from '@/utils/locations';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getReportTypeGate = (appliedFilters: AppliedFilters) => {
  const reportTypeValues = Object.values(ReportTypeCategory) as Array<string>;

  return (appliedFilters[FilterType.MORE_FILTERS] ?? []).reduce(
    (acc, filter) => {
      if (reportTypeValues.includes(String(filter.value))) {
        acc.noReportTypeSelected = false;

        if (filter.value === ReportTypeCategory.ANALYSIS) {
          acc.isAnalysisSelected = true;
        }
      }

      return acc;
    },
    {
      noReportTypeSelected: true,
      isAnalysisSelected: false,
    }
  );
};

const matchesCategory = (analysis: AnalysisListItem, appliedFilters: AppliedFilters) => {
  const selected = appliedFilters[FilterType.CATEGORY];

  if (!selected?.length) return true;

  const selectedIds = selected.map(({ value }) => Number(value));

  return Boolean(analysis.meta?.categories?.some(category => selectedIds.includes(category.id)));
};

const matchesImpact = (analysis: AnalysisListItem, appliedFilters: AppliedFilters) => {
  const impactValues = Object.values(ReportImpact) as Array<string>;
  const selectedImpacts = (appliedFilters[FilterType.MORE_FILTERS] ?? [])
    .filter(({ value }) => impactValues.includes(String(value)))
    .map(({ value }) => value as ReportImpact);

  if (!selectedImpacts.length) return true;

  return Boolean(analysis.meta?.impact && selectedImpacts.includes(analysis.meta.impact));
};

const getEventDateRange = (
  autoDateRange: AutoDateRange | null,
  startDate: string | null,
  endDate: string | null,
  now: number
): { start: number; end: number } | null => {
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const endOfToday = new Date(now).setHours(23, 59, 59, 999);

  switch (autoDateRange) {
    case AutoDateRange.TODAY:
      return {
        start: startOfToday,
        end: endOfToday,
      };
    case AutoDateRange.PAST_7_DAYS:
      return {
        start: now - 7 * DAY_IN_MS,
        end: endOfToday,
      };
    case AutoDateRange.PAST_30_DAYS:
      return {
        start: now - 30 * DAY_IN_MS,
        end: endOfToday,
      };
    case AutoDateRange.NEXT_7_DAYS:
      return {
        start: startOfToday,
        end: now + 7 * DAY_IN_MS,
      };
    case AutoDateRange.NEXT_30_DAYS:
      return {
        start: startOfToday,
        end: now + 30 * DAY_IN_MS,
      };
    case AutoDateRange.CUSTOM_TIMEFRAME:
      if (!startDate || !endDate) return null;

      return {
        start: new Date(startDate).getTime(),
        end: new Date(endDate).getTime(),
      };
    default:
      return null;
  }
};

const matchesTimeframe = (
  analysis: AnalysisListItem,
  appliedFilters: AppliedFilters,
  now: number
) => {
  const timeframe = appliedFilters[FilterType.TIMEFRAME];

  if (!timeframe?.length) return true;

  const { autoDateRange, publishedSinceSeconds, startDate, endDate } =
    getTimeFrameFromAppliedFilters(timeframe);

  if (publishedSinceSeconds != null) {
    if (!analysis.publicationDatetime) return false;

    return new Date(analysis.publicationDatetime).getTime() >= now - publishedSinceSeconds * 1000;
  }

  const eventRange = getEventDateRange(autoDateRange, startDate, endDate, now);

  if (!eventRange) return true;

  if (!analysis.meta?.eventDate) return false;

  const eventStart = new Date(analysis.meta.eventDate).getTime();
  const eventEnd = analysis.meta.eventEndDate
    ? new Date(analysis.meta.eventEndDate).getTime()
    : eventStart;

  return eventStart <= eventRange.end && eventEnd >= eventRange.start;
};

const matchesLocation = (analysis: AnalysisListItem, appliedFilters: AppliedFilters) => {
  const locationFilters = appliedFilters[FilterType.LOCATION];

  if (!locationFilters?.length) return true;

  const countryIds: Array<number> = [];
  const polygons: Array<Feature<Polygon>> = [];

  locationFilters.forEach(filter => {
    const transformed = getTransformedLocationFilterData(String(filter.value));

    if (transformed.locationItemType === LocationFilterItemType.COUNTRY) {
      countryIds.push((transformed as LocationCountryFilterItemValue).value.id);

      return;
    }

    if (
      transformed.locationItemType === LocationFilterItemType.GEOFENCE_RADIAL ||
      transformed.locationItemType === LocationFilterItemType.GEOFENCE_POLYGON
    ) {
      polygons.push((transformed as LocationGeofenceFilterItemValue).value as Feature<Polygon>);
    }
  });

  if (!countryIds.length && !polygons.length) return true;

  const matchesCountry = Boolean(
    countryIds.length && analysis.meta?.countries?.some(country => countryIds.includes(country.id))
  );

  if (matchesCountry) return true;

  if (!polygons.length) return false;

  const points = (analysis.meta?.locations ?? [])
    .filter(
      location => typeof location.longitude === 'number' && typeof location.latitude === 'number'
    )
    .map(location => [location.longitude, location.latitude] as Position);

  return points.some(point => polygons.some(polygon => booleanPointInPolygon(point, polygon)));
};

const matchesKeywords = (analysis: AnalysisListItem, appliedFilters: AppliedFilters) => {
  const keywords = appliedFilters[FilterType.KEYWORDS];

  if (!keywords?.length) return true;

  const title = analysis.title.toLowerCase();
  const parsed = keywords.map(({ value }) => {
    const [keyword, type] = String(value).split(' - ');

    return {
      keyword: keyword.trim().toLowerCase(),
      type,
    };
  });

  const andKeywords = parsed.filter(({ type }) => type === DropdownValues.AND);
  const orKeywords = parsed.filter(({ type }) => type === DropdownValues.OR);
  const exclKeywords = parsed.filter(({ type }) => type === DropdownValues.EXCL);

  if (exclKeywords.some(({ keyword }) => title.includes(keyword))) return false;

  if (!andKeywords.every(({ keyword }) => title.includes(keyword))) return false;

  if (orKeywords.length && !orKeywords.some(({ keyword }) => title.includes(keyword))) {
    return false;
  }

  return true;
};

export const getVisibleAnalyses = (
  analyses: Array<AnalysisListItem>,
  appliedFilters: AppliedFilters,
  now: number
): Array<AnalysisListItem> => {
  const { noReportTypeSelected, isAnalysisSelected } = getReportTypeGate(appliedFilters);

  if (!noReportTypeSelected && !isAnalysisSelected) return [];

  return analyses.filter(
    analysis =>
      matchesCategory(analysis, appliedFilters) &&
      matchesImpact(analysis, appliedFilters) &&
      matchesTimeframe(analysis, appliedFilters, now) &&
      matchesLocation(analysis, appliedFilters) &&
      matchesKeywords(analysis, appliedFilters)
  );
};
